import React, {useState, useEffect, useCallback, useMemo} from 'react'
import {useIntl} from 'react-intl'
import {KTCard, KTCardBody, Table, Loading} from '../../../components'
import {CustomCell} from '../../../components/Table/columns/CustomCell'
import {CustomCellDouble} from '../../../components/Table/columns/CustomCellDouble'
import {CustomHeader} from '../../../components/Table/columns/CustomHeader'
import {ListHeader} from '../../../components/Table/header/ListHeader'
import {ListLoading} from '../../../components/Table/ListLoading'
import {PageTitle} from '../../../core'

import {useDispatch, useSelector} from 'react-redux'
import {overtimeGetAll, overtimeExcel} from '../../../redux/slices/staff.slice'
import {staffGetAll} from '../../../redux/slices/lookup.slice'
import {useMemoizedStaticData} from '../../../utils/performanceOptimizations'

const selectStaffData = (state) => ({
  overtime: state.staff.overtime,
  loading: state.staff.loading,
})

const selectLookupData = (state) => ({
  staffLookup: state.lookup.staffLookup,
})

function List() {
  const intl = useIntl()
  const {overtime, loading} = useSelector(selectStaffData)
  const {staffLookup} = useSelector(selectLookupData)
  const dispatch = useDispatch()
  const pageKey = 'OVERTIME'

  const [params, setParams] = useState()

  useEffect(() => {
    if (params) dispatch(overtimeGetAll(params))
  }, [params, dispatch])

  useEffect(() => {
    dispatch(staffGetAll())
  }, [dispatch])

  const handleSorting = useCallback((item) => {
    setParams((prevState) => ({...prevState, sort: item && item.sort, order: item && item.order}))
  }, [])

  const handleSearch = useCallback((item) => {
    setParams((prevState) => ({...prevState, name: item}))
  }, [])

  const handlePagination = useCallback((pagination) => {
    setParams((prevState) => ({...prevState, ...pagination}))
  }, [])

  const handleExcelExport = useCallback(() => {
    dispatch(
      overtimeExcel({
        params,
        fileName: intl.formatMessage({id: 'BC.OVERTIME'}),
      })
    )
  }, [dispatch, params, intl])

  const handleFilterSubmit = useCallback((values) => {
    setParams({...values})
  }, [])

  const columns = useMemo(() => [
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'STAFF.SEARCH.NAMESURNAME'})}
          className='min-w-125px'
        />
      ),
      accessor: 'staffNameSurname',
      Cell: ({...props}) => <CustomCellDouble item={props} double='name' />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'STAFF.SEARCH.OVERTIME.DATE'})}
          className='min-w-125px'
        />
      ),
      accessor: 'startDate',
      Cell: ({...props}) => <CustomCell item={props} date='short' />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'STAFF.SEARCH.OVERTIME.TIME'})}
          className='min-w-125px'
        />
      ),
      accessor: 'hourDuration',
      Cell: ({...props}) => <CustomCell item={props} suffix='Saat' />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'LIST.LASTMODIFIEDINFO'})}
          className='min-w-125px'
        />
      ),
      accessor: 'lastModifiedBy',
      Cell: ({...props}) => <CustomCellDouble item={props} double='lastModifiedDate' />,
    },
  ], [intl, handleSorting])

  const actionButtons = useMemoizedStaticData(() => [
    {
      title: 'BUTTON.EXCEL',
      onClick: handleExcelExport,
      icon: '/images/icons/duotune/arrows/arr044.svg',
      variant: 'light-primary',
    },
    {
      title: 'FILTER',
      icon: '/images/icons/duotune/general/gen031.svg',
      variant: 'light-primary',
      list: [
        {
          title: 'STAFF.SEARCH.NAMESURNAME',
          data: staffLookup,
          name: 'staffId',
        },
      ],
    },
  ], [handleExcelExport, staffLookup])

  return (
    <>
      <PageTitle>{intl.formatMessage({id: 'BC.OVERTIME'})}</PageTitle>
      <KTCard>
        <ListHeader
          search={handleSearch}
          actionButtons={actionButtons}
          onSubmit={handleFilterSubmit}
          pages={pageKey}
        />
        <KTCardBody>
          {!overtime && <ListLoading />}
          {overtime && (
            <Table
              items={overtime}
              cols={columns}
              handlePagination={handlePagination}
              paddless
              pages={pageKey}
            />
          )}
        </KTCardBody>
      </KTCard>

      {loading && <Loading />}
    </>
  )
}
export {List}
